import { BRANDING_DEFAULT, type Branding } from "@copi/shared";
import { createContext, useContext, useLayoutEffect, useMemo, useState } from "react";

import { applyBranding } from "./apply-branding";

/**
 * Provider de branding (ADR 039 Fase 0 → ADR 040 Fase 1 del SaaS).
 *
 * Resuelve el branding desde una **fuente intercambiable** (`source`) y lo:
 *  - aplica al DOM (CSS variables + título) vía `applyBranding`, y
 *  - expone por contexto a los consumidores (`useBranding`).
 *
 * La costura está en `source`, que puede ser **sync o async**:
 *  - **Fase 0**: sincrónica (`() => BRANDING_DEFAULT`) ⇒ render inmediato, cero
 *    flash, sin estado de carga.
 *  - **Fase 1**: asíncrona por subdominio (`tenantSourceFase1`) ⇒ se arranca con
 *    el default ya aplicado (anti-flash) y se actualiza al resolver. **Ningún
 *    consumidor cambia** — siguen leyendo `useBranding()`.
 *
 * Debe montarse por FUERA de `AuthProvider`: el login ya es branded, así que el
 * branding tiene que resolver antes que la sesión.
 */

const BrandingContext = createContext<Branding | null>(null);
/**
 * Setter del branding activo. Contexto aparte para no cambiar la firma de
 * `useBranding()` (usada en decenas de sitios). Lo usa el panel de
 * personalización (ADR 047) para reflejar los cambios guardados en vivo en toda
 * la app, sin recargar.
 */
const BrandingSetterContext = createContext<((b: Branding) => void) | null>(null);

/** Fuente de branding. Sync (Fase 0) o async (Fase 1). */
export type BrandingSource = () => Branding | Promise<Branding>;

/** Fuente por defecto de Fase 0: el branding por defecto embebido. */
export const brandingSourceFase0: BrandingSource = () => BRANDING_DEFAULT;

interface BrandingProviderProps {
  children: React.ReactNode;
  /** Inyectable para tests y para la transición de fase. Default: Fase 0. */
  source?: BrandingSource;
}

export function BrandingProvider({
  children,
  source = brandingSourceFase0,
}: BrandingProviderProps) {
  // Inicialización: si la source es sincrónica, su valor se usa de una; si es
  // async (devuelve Promise), se arranca con el default y se actualiza al
  // resolver el effect. Así el primer paint nunca queda sin branding.
  const [branding, setBranding] = useState<Branding>(() => {
    const inicial = source();
    return inicial instanceof Promise ? BRANDING_DEFAULT : inicial;
  });

  // Resolución async: sólo corre trabajo si la source es una Promise. Para una
  // source sync esto es un no-op (ya se aplicó en el estado inicial).
  useLayoutEffect(() => {
    let cancelado = false;
    const resultado = source();
    if (resultado instanceof Promise) {
      void resultado.then((b) => {
        if (!cancelado) setBranding(b);
      });
    }
    return () => {
      cancelado = true;
    };
    // `source` se pasa una vez por montaje del provider (estable); el effect
    // corre sólo al montar para no re-disparar el fetch en cada render.
  }, []);

  // Aplica al DOM cada vez que el branding resuelto cambia (inicial + async).
  useLayoutEffect(() => {
    applyBranding(branding);
  }, [branding]);

  const value = useMemo(() => branding, [branding]);

  return (
    <BrandingContext.Provider value={value}>
      <BrandingSetterContext.Provider value={setBranding}>
        {children}
      </BrandingSetterContext.Provider>
    </BrandingContext.Provider>
  );
}

/** Devuelve el branding activo. Lanza si se usa fuera del `BrandingProvider`. */
export function useBranding(): Branding {
  const ctx = useContext(BrandingContext);
  if (!ctx) {
    throw new Error("useBranding debe usarse dentro de <BrandingProvider>");
  }
  return ctx;
}

/**
 * Devuelve un setter para reemplazar el branding activo en vivo (panel de
 * personalización). Lanza si se usa fuera del `BrandingProvider`.
 */
export function useSetBranding(): (b: Branding) => void {
  const ctx = useContext(BrandingSetterContext);
  if (!ctx) {
    throw new Error("useSetBranding debe usarse dentro de <BrandingProvider>");
  }
  return ctx;
}
